/* ============================================================
   BB Collection Filter / Search / Sort engine
   BELLA n' BEAR — client-side, dependency-free.
   ============================================================ */
(function () {
  'use strict';

  var SVG = {
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>',
    chev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>'
  };

  // colour swatch lookup (best-effort visual dot)
  var SWATCH = {
    black: '#000', white: '#fff', ivory: '#f7f3ea', cream: '#f3ead2', beige: '#e8dcc7',
    nude: '#e3c4a8', tan: '#d2a679', brown: '#6f4e37', chocolate: '#4e342e', camel: '#c19a6b', neutrals: '#d8ccbb',
    grey: '#9e9e9e', gray: '#9e9e9e', charcoal: '#36454f', silver: '#c0c0c0',
    navy: '#1f2d52', blue: '#2f5fbf', 'light blue': '#a7c7e7', teal: '#0f7d7d', turquoise: '#30d5c8',
    green: '#3c763d', olive: '#708238', khaki: '#b0a160', mint: '#a8e6cf', sage: '#9caf88',
    red: '#c0392b', burgundy: '#6d1f2b', maroon: '#6d1f2b', wine: '#722f37', rust: '#b7410e',
    orange: '#e67e22', coral: '#ff6f61', peach: '#ffcba4', apricot: '#fbceb1',
    pink: '#ec9ec0', blush: '#f3c5c5', rose: '#d98695', fuchsia: '#c0397a', magenta: '#c71585',
    purple: '#7d3c98', lilac: '#c8a2c8', lavender: '#b497bd', violet: '#8f00ff',
    yellow: '#f1c40f', gold: '#d4af37', mustard: '#d9a404', lime: '#bfd641',
    multicolour: 'linear-gradient(135deg,#f00,#ff0 25%,#0f0 50%,#0ff 75%,#00f)',
    multicolor: 'linear-gradient(135deg,#f00,#ff0 25%,#0f0 50%,#0ff 75%,#00f)',
    print: 'linear-gradient(135deg,#ddd,#999)', floral: 'linear-gradient(135deg,#f3c5c5,#9caf88)',
    animal: 'linear-gradient(135deg,#c19a6b,#4e342e)', leopard: 'linear-gradient(135deg,#c19a6b,#4e342e)'
  };

  // Recognised garment lengths (sourced from product tags). Order = display order.
  var LENGTH_ORDER = ['mini', 'midi', 'maxi'];
  var LENGTH_LABEL = { mini: 'Mini', midi: 'Midi', maxi: 'Maxi' };

  // Recognised garment styles (sourced from product tags). Order = display order.
  var STYLE_ORDER = ['long sleeve', 'strapless'];
  var STYLE_LABEL = { 'long sleeve': 'Long Sleeve', 'strapless': 'Strapless' };

  // Curated category buckets mapped from the raw product "type" field.
  var CATEGORY_ORDER = ['Dresses', 'Tops', 'Jackets & Blazers', 'Bottoms', 'Knitwear', 'Shoes', 'Accessories'];
  var CATEGORY_MAP = {
    maxi: 'Dresses', midi: 'Dresses', mini: 'Dresses',
    tops: 'Tops', 't-shirts': 'Tops', bodysuits: 'Tops',
    blazers: 'Jackets & Blazers', jackets: 'Jackets & Blazers', vests: 'Jackets & Blazers',
    jeans: 'Bottoms', pants: 'Bottoms', shorts: 'Bottoms',
    jumpers: 'Knitwear',
    heels: 'Shoes', boots: 'Shoes', sneakers: 'Shoes', slides: 'Shoes',
    earrings: 'Accessories'
  };

  function norm(s) {
    return (s || '').toString().toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  function esc(s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function titleCase(s) {
    s = (s || '').toString().trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  }
  // Classify a size value into a labelled section.
  function sizeBucket(v) {
    var n = norm(v);
    if (/[a-z]/.test(n)) {
      if (n === 'o/s' || n === 'os' || n === 'one size') return 'One Size';
      return 'Clothing';
    }
    var num = parseFloat(v);
    if (!isNaN(num)) return num <= 34 ? 'Denim' : 'Shoes';
    return 'Other';
  }
  function setW(url, w) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + w);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  function debounce(fn, ms) {
    var t; return function () {
      var ctx = this, a = arguments;
      clearTimeout(t); t = setTimeout(function () { fn.apply(ctx, a); }, ms);
    };
  }

  function Engine(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.perPage = cfg.perPage || 24;
    this.products = [];
    this.facets = null;
    this.ready = false;
    this.state = {
      q: '',
      vendor: [], type: [], size: [], colour: [], length: [], category: [], style: [],
      pmin: null, pmax: null, inStock: false, onSale: false,
      sort: 'featured',
      currentPage: 1
    };
    this._pending = false;
    this._replacedGrid = false;
  }

  Engine.prototype.init = function () {
    var r = this.root;
    this.elSearch = r.querySelector('.bb-cf-search-input');
    this.elSearchClear = r.querySelector('.bb-cf-search-clear');
    this.elToggle = r.querySelector('.bb-cf-filter-toggle');
    this.elToggleCount = r.querySelector('.bb-cf-filter-toggle__count');
    this.elSort = r.querySelector('.bb-cf-sort select');
    this.elChips = r.querySelector('.bb-cf-chips');
    this.elCount = r.querySelector('.bb-cf-count');
    this.elClear = r.querySelector('.bb-cf-chip--clear');

    this.grid = r.querySelector('ul.product-grid');
    this.pagination = r.querySelector('.bb-cf-pagination');
    this.empty = r.querySelector('.bb-cf-empty');

    if (!this.grid || !this.elSearch) return;

    var self0 = this;
    if (this.empty) {
      var resetBtn = this.empty.querySelector('.bb-cf-empty__reset');
      if (resetBtn) resetBtn.addEventListener('click', function () { self0.clearAll(); });
    }

    if (this.elChips) {
      this.elChips.addEventListener('click', function (e) {
        var clearBtn = e.target.closest('.bb-cf-chip--clear');
        if (clearBtn) { self0.clearAll(); return; }
var rm = e.target.closest('.bb-cf-chip__remove');
        if (rm) {
          self0.removeChip(rm.getAttribute('data-kind'), rm.getAttribute('data-value'));
        }
      });
    }

    if (this.elClear) {
      this.elClear.addEventListener('click', function () { self0.clearAll(); });
    }

    this.buildDrawer();
    this.bind();
    this.parseURL();

    var self = this;
    var start = function () { self.loadIndex(); };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 1800 });
    } else {
      setTimeout(start, 400);
    }
  };

  /* ---------------- index loading ---------------- */
  Engine.prototype.loadIndex = function () {
    var self = this;

    var island = document.getElementById('bb-cf-data');
    var inline = null;
    if (island) {
      try { inline = JSON.parse(island.textContent); } catch (e) { inline = null; }
    }

    var hasInline = !!(inline && inline.products && inline.products.length);
    var inlinePage = inline && inline.page ? inline.page : 0;
    var inlinePages = inline && inline.pages ? inline.pages : 1;

    if (hasInline && inlinePage === 1 && inlinePages === 1) {
      self.ingest(inline.products);
      return;
    }

    if (hasInline && inlinePage === 1) {
      self.ingest(inline.products);
      if (self.cfg.indexUrl && inlinePages > 1) self.fetchRest(inlinePages);
      return;
    }

    if (!this.cfg.indexUrl) {
      if (hasInline) self.ingest(inline.products);
      return;
    }
    var built = this.buildFetchBase();
    // Only a page-1 inline island is a valid stand-in for the full catalogue.
    // On a deep native page the island holds the wrong slice (e.g. products
    // 251-293), so it must NEVER be ingested as if complete - that is the
    // "current page onwards" bug. With no safe seed we keep the native grid.
    var safeFallback = (hasInline && inlinePage === 1) ? inline.products : null;
    var fetchAll = function () {
      return self.getJSON(built.base).then(function (first) {
        if (!first || !first.products) return null;
        var all = first.products.slice();
        var pages = first.pages || 1;
        if (pages <= 1) return all;
        var jobs = [];
        for (var p = 2; p <= pages; p++) {
          jobs.push(self.getJSON(built.base + built.sep + 'page=' + p)
            .then(function (d) { return (d && d.products) || []; }));
        }
        return Promise.all(jobs).then(function (rest) {
          for (var i = 0; i < rest.length; i++) all = all.concat(rest[i]);
          return all;
        });
      });
    };
    fetchAll()
      .then(function (products) {
        if (products && products.length) return products;
        return new Promise(function (r) { setTimeout(r, 500); }).then(fetchAll);
      })
      .then(function (products) {
        if (products && products.length) {
          self.ingest(products);
        } else if (safeFallback) {
          self.ingest(safeFallback);
        } else if (window.console) {
          console.warn('[bb-cf] index fetch returned no products — keeping native grid');
        }
      })
      .catch(function (err) {
        if (safeFallback) self.ingest(safeFallback);
        if (window.console) console.warn('[bb-cf] index load failed', err);
      });
  };

  Engine.prototype.buildFetchBase = function () {
    var base = this.cfg.indexUrl;
    var cur = new URLSearchParams(location.search);
    var pass = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb', '_pos', 'key'];
    for (var pi = 0; pi < pass.length; pi++) {
      var pv = cur.get(pass[pi]);
      if (pv) base += (base.indexOf('?') === -1 ? '?' : '&') + pass[pi] + '=' + encodeURIComponent(pv);
    }
    return { base: base, sep: base.indexOf('?') === -1 ? '?' : '&' };
  };

  Engine.prototype.getJSON = function (url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) return null;
        return res.text().then(function (txt) {
          try { return JSON.parse(txt); } catch (e) { return null; }
        });
      })
      .catch(function () { return null; });
  };

  Engine.prototype.fetchRest = function (pages) {
    var self = this;
    var built = this.buildFetchBase();
    var jobs = [];
    for (var p = 2; p <= pages; p++) {
      jobs.push(self.getJSON(built.base + built.sep + 'page=' + p)
        .then(function (d) { return (d && d.products) || []; }));
    }
    Promise.all(jobs).then(function (rest) {
      var extra = [];
      for (var i = 0; i < rest.length; i++) extra = extra.concat(rest[i]);
      if (!extra.length) return;
      self.append(extra);
    }).catch(function () {});
  };

  Engine.prototype.normaliseProduct = function (p, order) {
    p._order = order;
    p._norm = norm(p.blob);
    p._sale = (p.cap && p.p && p.cap > p.p) || (p.caph && p.caph.length);
    var cs = [], seen = {};
    var src = p.colours || [];
    for (var j = 0; j < src.length; j++) {
      var c = (src[j] || '').toString().trim();
      if (!c) continue;
      var key = norm(c);
      if (seen[key]) continue;
      seen[key] = 1; cs.push(c);
    }
    p._colours = cs;
    p._coloursN = cs.map(norm);
    p._sizesAvail = [];
    if (p.sizes) for (var s = 0; s < p.sizes.length; s++) {
      if (p.sizes[s] && p.sizes[s].a) p._sizesAvail.push(p.sizes[s].s);
    }
    p._lengths = [];
    if (p.tags) for (var lt = 0; lt < p.tags.length; lt++) {
      var ln = norm(p.tags[lt]);
      if (LENGTH_LABEL[ln] && p._lengths.indexOf(ln) === -1) p._lengths.push(ln);
    }
    p._styles = [];
    if (p.tags) for (var stz = 0; stz < p.tags.length; stz++) {
      var sname = norm(p.tags[stz]);
      if (STYLE_LABEL[sname] && p._styles.indexOf(sname) === -1) p._styles.push(sname);
    }
    p._cat = p.pt ? (CATEGORY_MAP[norm(p.pt)] || titleCase(p.pt)) : null;
  };

  Engine.prototype.ingest = function (products) {
    if (!products || !products.length) {
      if (window.console) console.warn('[bb-cf] index returned no products — keeping native grid');
      return;
    }
    for (var i = 0; i < products.length; i++) this.normaliseProduct(products[i], i);
    this.products = products;
    this.buildFacets();
    this.fillDrawer();
    this.ready = true;
    this.activate();
    this.syncControls();
    if (this._pending) this._pending = false;
    this.refresh();
  };

  Engine.prototype.append = function (extra) {
    var have = {};
    for (var i = 0; i < this.products.length; i++) have[this.products[i].id] = 1;
    var base = this.products.length;
    var added = 0;
    for (var j = 0; j < extra.length; j++) {
      var p = extra[j];
      if (have[p.id]) continue;
      this.normaliseProduct(p, base + added);
      this.products.push(p);
      added++;
    }
    if (!added) return;
    this.buildFacets();
    this.fillDrawer();
    this.syncControls();
    if (document.body.classList.contains('bb-cf-active') && this.isFiltering()) this.render();
  };

  Engine.prototype.buildFacets = function () {
    var vendor = {}, type = {}, size = {}, colour = {}, colourCase = {}, length = {}, category = {}, style = {};
    var pmin = Infinity, pmax = 0;
    for (var i = 0; i < this.products.length; i++) {
      var p = this.products[i];
      if (p.v) vendor[p.v] = (vendor[p.v] || 0) + 1;
      if (p.pt) type[p.pt] = (type[p.pt] || 0) + 1;
      if (p._cat) category[p._cat] = (category[p._cat] || 0) + 1;
      for (var s = 0; s < p._sizesAvail.length; s++) {
        var sv = p._sizesAvail[s]; if (sv) size[sv] = (size[sv] || 0) + 1;
      }
      for (var c = 0; c < p._colours.length; c++) {
        var raw = p._colours[c], k = norm(raw);
        colour[k] = (colour[k] || 0) + 1;
        if (!colourCase[k]) colourCase[k] = titleCase(raw);
      }
      for (var lf = 0; lf < p._lengths.length; lf++) {
        length[p._lengths[lf]] = (length[p._lengths[lf]] || 0) + 1;
      }
      for (var yf = 0; yf < p._styles.length; yf++) {
        style[p._styles[yf]] = (style[p._styles[yf]] || 0) + 1;
      }
      if (typeof p.p === 'number') {
        if (p.p < pmin) pmin = p.p;
        if (p.p > pmax) pmax = p.p;
      }
    }
    var sizeOrder = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'os', 'one size'];
    var sizeKey = function (v) {
      var n = norm(v).replace(/[^a-z0-9 ]/g, '');
      var idx = sizeOrder.indexOf(n);
      if (idx !== -1) return [0, idx, n];
      var num = parseFloat(v);
      if (!isNaN(num)) return [1, num, n];
      return [2, 0, n];
    };
    this.facets = {
      vendor: this.sortCounts(vendor),
      type: this.sortCounts(type),
      category: (function () {
        var ordered = CATEGORY_ORDER.filter(function (cc) { return category[cc]; });
        Object.keys(category).sort().forEach(function (cc) { if (ordered.indexOf(cc) === -1) ordered.push(cc); });
        return ordered.map(function (cc) { return { value: cc, label: cc, count: category[cc] }; });
      })(),
      colour: Object.keys(colour).sort().map(function (k) {
        return { value: k, label: colourCase[k], count: colour[k] };
      }),
      size: Object.keys(size).map(function (k) { return k; }).sort(function (a, b) {
        var ka = sizeKey(a), kb = sizeKey(b);
        return ka[0] - kb[0] || ka[1] - kb[1] || (ka[2] < kb[2] ? -1 : 1);
      }).map(function (k) { return { value: k, label: k, count: size[k] }; }),
      length: LENGTH_ORDER.filter(function (k) { return length[k]; }).map(function (k) {
        return { value: k, label: LENGTH_LABEL[k], count: length[k] };
      }),
      style: STYLE_ORDER.filter(function (k) { return style[k]; }).map(function (k) {
        return { value: k, label: STYLE_LABEL[k], count: style[k] };
      }),
      pmin: pmin === Infinity ? 0 : Math.floor(pmin / 100),
      pmax: pmax === 0 ? 0 : Math.ceil(pmax / 100)
    };
  };

  Engine.prototype.sortCounts = function (obj) {
    return Object.keys(obj).sort().map(function (k) {
      return { value: k, label: k, count: obj[k] };
    });
  };

  /* ---------------- drawer ---------------- */
  Engine.prototype.buildDrawer = function () {
    var overlay = document.createElement('div');
    overlay.className = 'bb-cf-drawer-overlay';
    var drawer = document.createElement('aside');
    drawer.className = 'bb-cf-drawer';
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-label', 'Filter products');
    drawer.innerHTML =
      '<div class="bb-cf-drawer__head">' +
        '<h2 class="bb-cf-drawer__title">Filter</h2>' +
        '<button type="button" class="bb-cf-drawer__close" aria-label="Close filters">' + SVG.close + '</button>' +
      '</div>' +
      '<div class="bb-cf-drawer__body"></div>' +
      '<div class="bb-cf-drawer__foot">' +
        '<button type="button" class="bb-cf-foot-clear">Clear all</button>' +
        '<button type="button" class="bb-cf-foot-apply">View results</button>' +
      '</div>';
    document.body.appendChild(overlay);
    var mount = document.querySelector('[data-bb-cf-sidebar]');
    (mount || document.body).appendChild(drawer);
    this.overlay = overlay;
    this.drawer = drawer;
    this.drawerBody = drawer.querySelector('.bb-cf-drawer__body');
  };

  Engine.prototype.groupTpl = function (key, title, inner) {
    return '<div class="bb-cf-group" data-group="' + key + '">' +
      '<button type="button" class="bb-cf-group__head" aria-expanded="false">' +
        '<span class="bb-cf-group__title">' + esc(title) + '</span>' +
        '<span class="bb-cf-group__count" aria-hidden="true"></span>' +
        '<svg class="bb-cf-group__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>' +
      '</button>' +
      '<div class="bb-cf-group__panel">' + inner + '</div>' +
    '</div>';
  };

  Engine.prototype.optTpl = function (group, value, label, count) {
    return '<label class="bb-cf-opt">' +
      '<input type="checkbox" data-filter="' + group + '" value="' + esc(value) + '">' +
      '<span class="bb-cf-opt__box">' + SVG.check + '</span>' +
      '<span class="bb-cf-opt__label">' + esc(label) + '</span>' +
      '<span class="bb-cf-opt__count">' + count + '</span>' +
    '</label>';
  };

  Engine.prototype.fillDrawer = function () {
    var f = this.facets, html = '';

    if (f.size.length) {
      var sb = { 'Clothing': [], 'Denim': [], 'Shoes': [], 'One Size': [], 'Other': [] };
      for (var s = 0; s < f.size.length; s++) {
        var bk = sizeBucket(f.size[s].value);
        if (!sb[bk]) bk = 'Other';
        sb[bk].push(f.size[s]);
      }
      var sbOrder = ['Clothing', 'Denim', 'Shoes', 'One Size', 'Other'];
      var nonEmpty = 0;
      for (var be = 0; be < sbOrder.length; be++) if (sb[sbOrder[be]].length) nonEmpty++;
      var so = '';
      for (var bj = 0; bj < sbOrder.length; bj++) {
        var bn = sbOrder[bj], arr = sb[bn];
        if (!arr.length) continue;
        if (nonEmpty > 1) so += '<div class="bb-cf-subhead" style="font-size:0.6875rem;letter-spacing:0.08em;text-transform:uppercase;color:rgba(0,0,0,0.45);font-weight:500;margin:0.85rem 0 0.45rem;">' + esc(bn) + '</div>';
        so += '<div class="bb-cf-options">';
        for (var sk = 0; sk < arr.length; sk++) so += this.optTpl('size', arr[sk].value, arr[sk].label, arr[sk].count);
        so += '</div>';
      }
      html += this.groupTpl('size', 'Size', so);
    }

    if (f.length && f.length.length) {
      var lo = '<div class="bb-cf-options">';
      for (var lg = 0; lg < f.length.length; lg++) lo += this.optTpl('length', f.length[lg].value, f.length[lg].label, f.length[lg].count);
      lo += '</div>';
      html += this.groupTpl('length', 'Length', lo);
    }

    if (f.category && f.category.length > 1) {
      var co = '<div class="bb-cf-options">';
      for (var ca = 0; ca < f.category.length; ca++) co += this.optTpl('category', f.category[ca].value, f.category[ca].label, f.category[ca].count);
      co += '</div>';
      html += this.groupTpl('category', 'Category', co);
    }

    if (f.colour.length) {
      var sw = '<div class="bb-cf-swatches">';
      for (var i = 0; i < f.colour.length; i++) {
        var o = f.colour[i];
        var bg = SWATCH[o.value] || '#ddd';
        var stylev = 'background:' + bg;
        sw += '<label class="bb-cf-swatch">' +
          '<input type="checkbox" data-filter="colour" value="' + esc(o.value) + '">' +
          '<span class="bb-cf-swatch__dot" style="' + stylev + '"></span>' +
          '<span class="bb-cf-swatch__label">' + esc(o.label) + '</span>' +
          '</label>';
      }
      sw += '</div>';
      html += this.groupTpl('colour', 'Colour', sw);
    }

    if (f.vendor.length > 1) {
      var vo = '<div class="bb-cf-options">';
      for (var v = 0; v < f.vendor.length; v++) vo += this.optTpl('vendor', f.vendor[v].value, f.vendor[v].label, f.vendor[v].count);
      vo += '</div>';
      html += this.groupTpl('vendor', 'Designer', vo);
    }

    if (f.style && f.style.length) {
      var yo = '<div class="bb-cf-options">';
      for (var sg = 0; sg < f.style.length; sg++) yo += this.optTpl('style', f.style[sg].value, f.style[sg].label, f.style[sg].count);
      yo += '</div>';
      html += this.groupTpl('style', 'Style', yo);
    }

    var price = '<div class="bb-cf-price"><div class="bb-cf-price__inputs">' +
      '<span class="bb-cf-price__field"><span>$</span><input type="number" inputmode="numeric" class="bb-cf-price-min" placeholder="' + f.pmin + '" min="0"></span>' +
      '<span class="bb-cf-price__sep">&ndash;</span>' +
      '<span class="bb-cf-price__field"><span>$</span><input type="number" inputmode="numeric" class="bb-cf-price-max" placeholder="' + f.pmax + '" min="0"></span>' +
      '</div></div>';
    html += this.groupTpl('price', 'Price', price);

    html += '<div class="bb-cf-sale-pin">' +
      '<span class="bb-cf-sale-pin__label">On sale only</span>' +
      '<label class="bb-cf-switch">' +
        '<input type="checkbox" data-filter="onSale" value="1">' +
        '<span class="bb-cf-switch__track"><span class="bb-cf-switch__thumb"></span></span>' +
      '</label>' +
    '</div>';

    this.drawerBody.innerHTML = html;
    var sizeGroup = this.drawerBody.querySelector('.bb-cf-group[data-group="size"]');
    if (sizeGroup) {
      sizeGroup.classList.add('is-open');
      var sizeHead = sizeGroup.querySelector('.bb-cf-group__head');
      if (sizeHead) sizeHead.setAttribute('aria-expanded', 'true');
    }
    this.bindDrawer();
    this.updateGroupBadges();
    this.updateFacetCounts();
  };

  /* ---------------- binding ---------------- */
  Engine.prototype.bind = function () {
    var self = this;

    var onType = debounce(function () {
      self.state.q = self.elSearch.value.trim();
      self.elSearchClear.classList.toggle('is-visible', !!self.state.q);
      if (!self.ready) { self._pending = true; return; }
      self.state.currentPage = 1;
      self.refresh();
    }, 130);
    this.elSearch.addEventListener('input', onType);

    this.elSearchClear.addEventListener('click', function () {
      self.elSearch.value = ''; self.state.q = '';
      self.elSearchClear.classList.remove('is-visible');
      self.elSearch.focus();
      if (!self.ready) return;
      self.state.currentPage = 1; self.refresh();
    });

    if (this.elSort) {
      this.elSort.addEventListener('change', function () {
        self.state.sort = self.elSort.value;
        if (!self.ready) { self._pending = true; return; }
        self.state.currentPage = 1; self.refresh();
      });
    }

    this.elToggle.addEventListener('click', function () { self.openDrawer(); });
  };

  Engine.prototype.bindDrawer = function () {
    var self = this;
    this.overlay.addEventListener('click', function () { self.closeDrawer(); });
    this.drawer.querySelector('.bb-cf-drawer__close').addEventListener('click', function () { self.closeDrawer(); });
    this.drawer.querySelector('.bb-cf-foot-apply').addEventListener('click', function () { self.closeDrawer(); });
    this.drawer.querySelector('.bb-cf-foot-clear').addEventListener('click', function () { self.clearAll(); });

    var heads = this.drawerBody.querySelectorAll('.bb-cf-group__head');
    for (var i = 0; i < heads.length; i++) {
      heads[i].addEventListener('click', function () {
        var grp = this.closest('.bb-cf-group');
        var willOpen = !grp.classList.contains('is-open');
        var all = self.drawerBody.querySelectorAll('.bb-cf-group');
        for (var z = 0; z < all.length; z++) {
          all[z].classList.remove('is-open');
          var hh = all[z].querySelector('.bb-cf-group__head');
          if (hh) hh.setAttribute('aria-expanded', 'false');
        }
        if (willOpen) {
          grp.classList.add('is-open');
          this.setAttribute('aria-expanded', 'true');
          requestAnimationFrame(function () {
            var gr = grp.getBoundingClientRect();
            var dr = self.drawerBody.getBoundingClientRect();
            self.drawerBody.scrollTo({ top: self.drawerBody.scrollTop + (gr.top - dr.top), behavior: 'smooth' });
          });
        }
      });
    }

    this.drawerBody.addEventListener('change', function (e) {
      var inp = e.target;
      if (inp.type !== 'checkbox' && inp.type !== 'number') return;
      var grp = inp.getAttribute('data-filter');
      if (inp.type === 'checkbox') {
        if (grp === 'inStock') { self.state.inStock = inp.checked; }
        else if (grp === 'onSale') { self.state.onSale = inp.checked; }
        else {
          var arr = self.state[grp];
          if (!arr) return;
          var val = inp.value;
          var idx = arr.indexOf(val);
          if (inp.checked && idx === -1) arr.push(val);
          else if (!inp.checked && idx !== -1) arr.splice(idx, 1);
        }
      }
      self.state.currentPage = 1;
      self.refresh();
    });

    var onPrice = debounce(function () {
      var mn = self.drawerBody.querySelector('.bb-cf-price-min');
      var mx = self.drawerBody.querySelector('.bb-cf-price-max');
      self.state.pmin = mn.value !== '' ? parseFloat(mn.value) : null;
      self.state.pmax = mx.value !== '' ? parseFloat(mx.value) : null;
      self.state.currentPage = 1;
      self.refresh();
    }, 250);
    this.drawerBody.addEventListener('input', function (e) {
      if (e.target.type === 'number') onPrice();
    });

    this.drawer.addEventListener('keydown', function (e) {
      if (!self.drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') { self.closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      var foc = self.drawer.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
      var list = [];
      for (var i = 0; i < foc.length; i++) if (foc[i].offsetParent !== null) list.push(foc[i]);
      if (!list.length) return;
      var first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  };

  Engine.prototype.openDrawer = function () {
    this._lastFocus = document.activeElement;
    this.overlay.classList.add('is-open');
    this.drawer.classList.add('is-open');
    document.body.classList.add('bb-cf-noscroll');
    var c = this.drawer.querySelector('.bb-cf-drawer__close');
    if (c) c.focus();
  };
  Engine.prototype.closeDrawer = function () {
    this.overlay.classList.remove('is-open');
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('bb-cf-noscroll');
    if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
  };

  /* ---------------- active-state management ---------------- */
  Engine.prototype.isFiltering = function () {
    var st = this.state;
    return !!(st.q || st.vendor.length || st.type.length || st.size.length ||
      st.colour.length || st.length.length || st.category.length || st.style.length ||
      st.pmin != null || st.pmax != null ||
      st.inStock || st.onSale || (st.sort && st.sort !== 'featured'));
  };

  Engine.prototype.nativePag = function () {
    return this.root.querySelector('.pagination, .pagination-controls');
  };

  Engine.prototype.deactivate = function () {
    document.body.classList.remove('bb-cf-active');
    // Only restore the native grid if the engine actually replaced it (an
    // engine render — including the empty-results state — sets _replacedGrid).
    // On first load, and any unfiltered state we never rendered into, the grid
    // is still the server-rendered native markup, so re-writing innerHTML here
    // is a pointless full re-render that blanks and reloads every card image:
    // the visible load "snap". Guarding it keeps first paint stable.
    if (this._replacedGrid && this._nativeGrid != null) {
      this.grid.innerHTML = this._nativeGrid;
      this._replacedGrid = false;
    }
    if (this.pagination) this.pagination.innerHTML = '';
    if (this.empty) this.empty.classList.remove('is-visible');
    if (this.elCount) this.elCount.textContent = '';
    var np = this.nativePag();
    if (np) np.style.display = '';
    this.renderChips();
    this.updateFilterCount();
  };

  Engine.prototype.activate = function () {
    if (this._nativeGrid == null) this._nativeGrid = this.grid.innerHTML;
  };

  Engine.prototype.refresh = function () {
    if (!this.ready) return;
    if (this.isFiltering()) {
      document.body.classList.add('bb-cf-active');
      var np = this.nativePag();
      if (np) np.style.display = 'none';
      this.render();
    } else {
      this.deactivate();
      this.syncURL();
    }
  };

  /* ---------------- filtering / sorting ---------------- */
  Engine.prototype._filterCtx = function () {
    var st = this.state;
    var rawTerms = st.q ? norm(st.q).split(/\s+/).filter(Boolean) : [];
    // Colour-aware search (UNION, never narrower than plain text): a typed word
    // that matches a real swatch is satisfied by EITHER a text/blob hit OR a
    // colour-tag hit. It is NOT swapped out of the text search - doing that used
    // to collapse e.g. pink to only the colour-tagged items (8) and silently
    // drop the title/description matches (26). Explicit colour chips stay in
    // effColours as their own strict group.
    var colourSet = {};
    if (this.facets && this.facets.colour) {
      for (var ci = 0; ci < this.facets.colour.length; ci++) colourSet[this.facets.colour[ci].value] = 1;
    }
    var terms = [], colourTerms = [];
    for (var rt = 0; rt < rawTerms.length; rt++) {
      if (colourSet[rawTerms[rt]]) colourTerms.push(rawTerms[rt]);
      else terms.push(rawTerms[rt]);
    }
    return {
      terms: terms,
      colourTerms: colourTerms,
      effColours: st.colour.slice(),
      pmin: st.pmin != null ? st.pmin * 100 : null,
      pmax: st.pmax != null ? st.pmax * 100 : null
    };
  };

  Engine.prototype._passes = function (p, ctx, exclude) {
    var st = this.state;
    if (ctx.terms.length) {
      for (var t = 0; t < ctx.terms.length; t++) {
        if (p._norm.indexOf(ctx.terms[t]) === -1) return false;
      }
    }
    // Typed colour words: a blob/text hit OR a colour-tag hit satisfies the word.
    if (ctx.colourTerms && ctx.colourTerms.length) {
      for (var ctm = 0; ctm < ctx.colourTerms.length; ctm++) {
        var cw = ctx.colourTerms[ctm];
        if (p._norm.indexOf(cw) === -1 && p._coloursN.indexOf(cw) === -1) return false;
      }
    }
    if (ctx.pmin != null && p.p < ctx.pmin) return false;
    if (ctx.pmax != null && p.p > ctx.pmax) return false;
    if (st.onSale && !p._sale) return false;
    if (st.inStock && !p.av) return false;
    if (exclude !== 'vendor' && st.vendor.length && st.vendor.indexOf(p.v) === -1) return false;
    if (exclude !== 'category' && st.category.length && (!p._cat || st.category.indexOf(p._cat) === -1)) return false;
    if (exclude !== 'size' && st.size.length) {
      var hasSize = false;
      for (var s = 0; s < p._sizesAvail.length; s++) { if (st.size.indexOf(p._sizesAvail[s]) !== -1) { hasSize = true; break; } }
      if (!hasSize) return false;
    }
    if (exclude !== 'colour' && ctx.effColours.length) {
      var hasCol = false;
      for (var c = 0; c < p._coloursN.length; c++) { if (ctx.effColours.indexOf(p._coloursN[c]) !== -1) { hasCol = true; break; } }
      if (!hasCol) return false;
    }
    if (exclude !== 'length' && st.length.length) {
      var hasLen = false;
      for (var lz = 0; lz < p._lengths.length; lz++) { if (st.length.indexOf(p._lengths[lz]) !== -1) { hasLen = true; break; } }
      if (!hasLen) return false;
    }
    if (exclude !== 'style' && st.style.length) {
      var hasSty = false;
      for (var sy = 0; sy < p._styles.length; sy++) { if (st.style.indexOf(p._styles[sy]) !== -1) { hasSty = true; break; } }
      if (!hasSty) return false;
    }
    return true;
  };

  Engine.prototype.filtered = function () {
    var ctx = this._filterCtx();
    var out = [];
    for (var i = 0; i < this.products.length; i++) {
      if (this._passes(this.products[i], ctx, null)) out.push(this.products[i]);
    }
    this.sortList(out);
    return out;
  };

  Engine.prototype.sortList = function (arr) {
    var s = this.state.sort;
    if (s === 'price-asc') arr.sort(function (a, b) { return a.p - b.p; });
    else if (s === 'price-desc') arr.sort(function (a, b) { return b.p - a.p; });
    else if (s === 'title-asc') arr.sort(function (a, b) { return a.t.localeCompare(b.t); });
    else if (s === 'title-desc') arr.sort(function (a, b) { return b.t.localeCompare(a.t); });
    else if (s === 'newest') arr.sort(function (a, b) { return (b.created || 0) - (a.created || 0); });
    else arr.sort(function (a, b) { return a._order - b._order; });
  };

  /* ---------------- render (engine results) ---------------- */
  Engine.prototype.render = function () {
    if (!this.ready) return;
    var list = this.filtered();
    this._lastList = list;
    var total = list.length;
    var pages = Math.max(1, Math.ceil(total / this.perPage));
    if (this.state.currentPage > pages) this.state.currentPage = pages;
    if (this.state.currentPage < 1) this.state.currentPage = 1;
    var page = this.state.currentPage;
    var startIdx = (page - 1) * this.perPage;
    var endIdx = Math.min(startIdx + this.perPage, total);

    if (this.elCount) {
      this.elCount.textContent = total === 1 ? '1 product' : (total + ' products');
    }

    if (total === 0) {
      this.grid.innerHTML = '';
      this._replacedGrid = true;
      if (this.empty) this.empty.classList.add('is-visible');
      if (this.pagination) this.pagination.innerHTML = '';
      this.renderChips();
      this.updateFilterCount();
      this.syncURL();
      return;
    }
    if (this.empty) this.empty.classList.remove('is-visible');

    var html = '';
    for (var i = startIdx; i < endIdx; i++) html += this.card(list[i], i);
    this.grid.innerHTML = html;
    this._replacedGrid = true;

    this.renderPagination(pages, page);
    this.renderChips();
    this.updateFilterCount();
    this.syncURL();
  };

  Engine.prototype.renderPagination = function (pages, page) {
    if (!this.pagination) return;
    if (pages <= 1) { this.pagination.innerHTML = ''; return; }
    var self = this;

    var nums = [];
    var add = function (n) { if (nums.indexOf(n) === -1) nums.push(n); };
    add(1);
    for (var d = -1; d <= 1; d++) { var n = page + d; if (n >= 1 && n <= pages) add(n); }
    add(pages);
    nums.sort(function (a, b) { return a - b; });

    var html = '';
    html += '<button type="button" class="bb-cf-page bb-cf-page--prev" data-page="' + (page - 1) + '"' +
            (page <= 1 ? ' disabled' : '') + ' aria-label="Previous page">' + SVG.arrow + '</button>';
    var prev = 0;
    for (var i = 0; i < nums.length; i++) {
      var nn = nums[i];
      if (prev && nn - prev > 1) html += '<span class="bb-cf-page-gap">…</span>';
      html += '<button type="button" class="bb-cf-page' + (nn === page ? ' is-current' : '') +
              '" data-page="' + nn + '"' + (nn === page ? ' aria-current="page"' : '') + '>' + nn + '</button>';
      prev = nn;
    }
    html += '<button type="button" class="bb-cf-page bb-cf-page--next" data-page="' + (page + 1) + '"' +
            (page >= pages ? ' disabled' : '') + ' aria-label="Next page">' + SVG.arrow + '</button>';
    this.pagination.innerHTML = html;

    var btns = this.pagination.querySelectorAll('.bb-cf-page');
    for (var b = 0; b < btns.length; b++) {
      btns[b].addEventListener('click', function () {
        if (this.disabled) return;
        var target = parseInt(this.getAttribute('data-page'), 10);
        if (!isNaN(target)) self.goToPage(target);
      });
    }
  };

  Engine.prototype.goToPage = function (n) {
    this.state.currentPage = n;
    this.render();
    var top = this.root.getBoundingClientRect().top + window.pageYOffset - 20;
    window.scrollTo({ top: top, behavior: 'smooth' });
  };

  Engine.prototype.card = function (p, i) {
    var media = '';
    if (p.img) {
      var src = setW(p.img, 540);
      var srcset = setW(p.img, 360) + ' 360w, ' + setW(p.img, 540) + ' 540w, ' +
                   setW(p.img, 720) + ' 720w, ' + setW(p.img, 1080) + ' 1080w';
      var hover = '';
      if (p.img2) {
        hover = '<img class="bb-cf-media__hover" src="' + esc(setW(p.img2, 540)) + '" alt="" loading="lazy" decoding="async">';
      }
      media = '<img class="bb-cf-media__main" src="' + esc(src) + '" srcset="' + esc(srcset) +
              '" sizes="(min-width:1200px) 25vw, (min-width:750px) 33vw, 50vw" alt="' + esc(p.t) +
              '" loading="lazy" decoding="async">' + hover;
    }

    var price;
    if (p._sale && p.caph) {
      price = '<span class="bb-cf-price__sale">' + p.ph + '</span>' +
              '<span class="bb-cf-price__compare">' + p.caph + '</span>';
    } else {
      price = '<span>' + p.ph + '</span>';
    }

    var sizes = '';
    if (p.sizes && p.sizes.length) {
      var firstAvail = -1;
      for (var k = 0; k < p.sizes.length; k++) { if (p.sizes[k].a) { firstAvail = k; break; } }
      var btns = '';
      for (var s = 0; s < p.sizes.length; s++) {
        var sz = p.sizes[s];
        var cls = 'bb-cg-btn';
        if (s === firstAvail) cls += ' bb-cg-selected';
        if (!sz.a) cls += ' bb-cg-soldout';
        btns += '<button class="' + cls + '" type="button" data-bb-cg-trigger data-variant-id="' + esc(sz.id) +
                '" data-size="' + esc(sz.s) + '"' + (sz.a ? '' : ' disabled') + '>' +
                '<span class="bb-cg-label">' + esc(sz.s) + '</span>' +
                '<span class="bb-cg-spinner"></span></button>';
      }
      sizes = '<div class="bb-cg-sizes" data-size-count="' + p.sizes.length + '">' + btns + '</div>';
    }

    return '<li class="product-grid__item bb-cf-card" data-product-id="' + esc(p.id) + '">' +
      '<a class="bb-cf-card__link" href="' + esc(p.u) + '">' +
        '<span class="bb-cf-media">' + media + '</span>' +
        '<span class="bb-cf-info">' +
          (p.v ? '<span class="bb-cf-vendor">' + esc(p.v) + '</span>' : '') +
          '<span class="bb-cf-title">' + esc(p.t) + '</span>' +
          '<span class="bb-cf-price">' + price + '</span>' +
        '</span>' +
      '</a>' + sizes +
      '</li>';
  };

  /* ---------------- chips ---------------- */
  Engine.prototype.renderChips = function () {
    if (!this.elChips) return;
    var st = this.state, chips = [];
    var add = function (kind, value, label) { chips.push({ kind: kind, value: value, label: label }); };

    if (st.q) add('q', '', 'Search: “' + st.q + '”');
    st.size.forEach(function (v) { add('size', v, 'Size ' + v); });
    st.length.forEach(function (v) { add('length', v, LENGTH_LABEL[v] || titleCase(v)); });
    st.category.forEach(function (v) { add('category', v, v); });
    st.colour.forEach(function (v) { add('colour', v, titleCase(v)); });
    st.vendor.forEach(function (v) { add('vendor', v, v); });
    st.style.forEach(function (v) { add('style', v, STYLE_LABEL[v] || titleCase(v)); });
    if (st.pmin != null || st.pmax != null) {
      var lbl = 'Price ' + (st.pmin != null ? '$' + st.pmin : '$0') + '–' + (st.pmax != null ? '$' + st.pmax : 'max');
      add('price', '', lbl);
    }
    if (st.onSale) add('onSale', '', 'On sale');

if (!chips.length) {
      this.elChips.className = 'bb-cf-chips';
      this.elChips.innerHTML = '';
      if (this.elClear) this.elClear.hidden = true;
      return;
    }
    var html = '';
    for (var i = 0; i < chips.length; i++) {
      html += '<span class="bb-cf-chip"><span class="bb-cf-chip__label">' + esc(chips[i].label) + '</span>' +
        '<button type="button" class="bb-cf-chip__remove" data-kind="' + chips[i].kind +
        '" data-value="' + esc(chips[i].value) + '" aria-label="Remove ' + esc(chips[i].label) + '">' +
        SVG.close + '</button></span>';
    }
this.elChips.className = 'bb-cf-chips is-visible';
    this.elChips.innerHTML = html;
    if (this.elClear) this.elClear.hidden = false;
  };

  Engine.prototype.removeChip = function (kind, value) {
    var st = this.state;
    if (kind === 'q') { st.q = ''; this.elSearch.value = ''; this.elSearchClear.classList.remove('is-visible'); }
    else if (kind === 'price') { st.pmin = null; st.pmax = null; this.syncControls(); }
    else if (kind === 'inStock') { st.inStock = false; }
    else if (kind === 'onSale') { st.onSale = false; }
    else if (st[kind]) {
      var idx = st[kind].indexOf(value);
      if (idx !== -1) st[kind].splice(idx, 1);
    }
    st.currentPage = 1;
    this.syncControls();
    this.refresh();
  };

  Engine.prototype.clearAll = function () {
    this.state = {
      q: '', vendor: [], type: [], size: [], colour: [], length: [], category: [], style: [],
      pmin: null, pmax: null, inStock: false, onSale: false,
      sort: this.state.sort,
      currentPage: 1
    };
    this.elSearch.value = '';
    this.elSearchClear.classList.remove('is-visible');
    this.syncControls();
    this.refresh();
  };

  Engine.prototype.syncControls = function () {
    if (!this.drawerBody) return;
    var st = this.state;
    var boxes = this.drawerBody.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < boxes.length; i++) {
      var grp = boxes[i].getAttribute('data-filter'), val = boxes[i].value;
      if (grp === 'inStock') boxes[i].checked = st.inStock;
      else if (grp === 'onSale') boxes[i].checked = st.onSale;
      else if (st[grp]) boxes[i].checked = st[grp].indexOf(val) !== -1;
    }
    var mn = this.drawerBody.querySelector('.bb-cf-price-min');
    var mx = this.drawerBody.querySelector('.bb-cf-price-max');
    if (mn) mn.value = st.pmin != null ? st.pmin : '';
    if (mx) mx.value = st.pmax != null ? st.pmax : '';
    if (this.elSort) this.elSort.value = st.sort;
  };

  Engine.prototype.updateFilterCount = function () {
    var st = this.state, n = 0;
    n += st.vendor.length + st.size.length + st.colour.length + st.length.length + st.category.length + st.style.length;
    if (st.pmin != null || st.pmax != null) n++;
    if (st.inStock) n++;
    if (st.onSale) n++;
    if (this.elToggleCount) this.elToggleCount.textContent = n;
    if (this.elToggle) this.elToggle.classList.toggle('has-active', n > 0);
    this.updateGroupBadges();
    this.updateFacetCounts();
  };

  Engine.prototype.updateGroupBadges = function () {
    if (!this.drawerBody) return;
    var st = this.state;
    var counts = {
      category: st.category.length,
      vendor: st.vendor.length,
      size: st.size.length,
      length: st.length.length,
      style: st.style.length,
      colour: st.colour.length,
      price: (st.pmin != null || st.pmax != null) ? 1 : 0
    };
    var groups = this.drawerBody.querySelectorAll('.bb-cf-group');
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i].getAttribute('data-group');
      var badge = groups[i].querySelector('.bb-cf-group__count');
      if (!badge) continue;
      var c = counts[g] || 0;
      if (c > 0) { badge.textContent = c; badge.classList.add('is-active'); }
      else { badge.textContent = ''; badge.classList.remove('is-active'); }
    }
  };

  Engine.prototype.updateFacetCounts = function () {
    if (!this.drawerBody || !this.facets) return;
    var ctx = this._filterCtx();
    var counts = { size: {}, length: {}, category: {}, colour: {}, vendor: {}, style: {} };
    for (var i = 0; i < this.products.length; i++) {
      var p = this.products[i];
      if (this._passes(p, ctx, 'size')) for (var a = 0; a < p._sizesAvail.length; a++) counts.size[p._sizesAvail[a]] = (counts.size[p._sizesAvail[a]] || 0) + 1;
      if (this._passes(p, ctx, 'length')) for (var b = 0; b < p._lengths.length; b++) counts.length[p._lengths[b]] = (counts.length[p._lengths[b]] || 0) + 1;
      if (this._passes(p, ctx, 'category') && p._cat) counts.category[p._cat] = (counts.category[p._cat] || 0) + 1;
      if (this._passes(p, ctx, 'colour')) for (var d = 0; d < p._coloursN.length; d++) counts.colour[p._coloursN[d]] = (counts.colour[p._coloursN[d]] || 0) + 1;
      if (this._passes(p, ctx, 'vendor') && p.v) counts.vendor[p.v] = (counts.vendor[p.v] || 0) + 1;
      if (this._passes(p, ctx, 'style')) for (var e = 0; e < p._styles.length; e++) counts.style[p._styles[e]] = (counts.style[p._styles[e]] || 0) + 1;
    }
    var opts = this.drawerBody.querySelectorAll('.bb-cf-opt input[data-filter]');
    for (var o = 0; o < opts.length; o++) {
      var inp = opts[o], grp = inp.getAttribute('data-filter'), val = inp.value;
      if (!counts[grp]) continue;
      var c2 = counts[grp][val] || 0;
      var label = inp.closest('.bb-cf-opt');
      if (!label) continue;
      var cspan = label.querySelector('.bb-cf-opt__count');
      if (cspan) cspan.textContent = c2;
      if (c2 === 0 && !inp.checked) label.classList.add('bb-cf-zero');
      else label.classList.remove('bb-cf-zero');
    }
    var sws = this.drawerBody.querySelectorAll('.bb-cf-swatch input[data-filter="colour"]');
    for (var w = 0; w < sws.length; w++) {
      var si = sws[w], sv = si.value, sl = si.closest('.bb-cf-swatch');
      if (!sl) continue;
      var sc = counts.colour[sv] || 0;
      if (sc === 0 && !si.checked) sl.classList.add('bb-cf-zero');
      else sl.classList.remove('bb-cf-zero');
    }
  };

  /* ---------------- URL sync ---------------- */
  Engine.prototype.syncURL = function () {
    if (!window.history || !history.replaceState) return;
    var st = this.state, p = new URLSearchParams();
    if (st.q) p.set('q', st.q);
    if (st.vendor.length) p.set('brand', st.vendor.join(','));
    if (st.category.length) p.set('category', st.category.join(','));
    if (st.size.length) p.set('size', st.size.join(','));
    if (st.colour.length) p.set('colour', st.colour.join(','));
    if (st.length.length) p.set('length', st.length.join(','));
    if (st.style.length) p.set('style', st.style.join(','));
    if (st.pmin != null) p.set('pmin', st.pmin);
    if (st.pmax != null) p.set('pmax', st.pmax);
    if (st.onSale) p.set('sale', '1');
    if (st.sort && st.sort !== 'featured') p.set('sort', st.sort);
    if (st.currentPage && st.currentPage > 1) p.set('pg', st.currentPage);
    var qs = p.toString();
    var url = location.pathname + (qs ? '?' + qs : '');
    history.replaceState(null, '', url);
  };

  Engine.prototype.parseURL = function () {
    var p = new URLSearchParams(location.search), st = this.state;
    var csv = function (k) { var v = p.get(k); return v ? v.split(',').filter(Boolean) : []; };
    if (p.get('q')) { st.q = p.get('q'); this.elSearch.value = st.q; this.elSearchClear.classList.add('is-visible'); }
    st.vendor = csv('brand'); st.size = csv('size'); st.colour = csv('colour'); st.length = csv('length'); st.category = csv('category'); st.style = csv('style');
    if (p.get('pmin')) st.pmin = parseFloat(p.get('pmin'));
    if (p.get('pmax')) st.pmax = parseFloat(p.get('pmax'));
    if (p.get('sale')) st.onSale = true;
    if (p.get('sort')) { st.sort = p.get('sort'); if (this.elSort) this.elSort.value = st.sort; }
    var pgRaw = p.get('pg') || p.get('page'); if (pgRaw) { var pg = parseInt(pgRaw, 10); if (!isNaN(pg) && pg > 0) st.currentPage = pg; }
  };

  /* ---------------- boot ---------------- */
  function boot() {
    var root = document.querySelector('[data-bb-cf-root]');
    var cfgEl = document.getElementById('bb-cf-config');
    if (!root || !cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { cfg = {}; }
    var engine = new Engine(root, cfg);
    engine.init();
    window.BBCollectionFilter = engine;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
